/*
 * Deferred, adaptive autoplay for [data-lazy-video] (rendered by snippets/video-lazy.liquid).
 * Loaded ONCE globally from layout/theme.liquid.
 *
 * Each video stays preload="none" with its sources in data-src until it scrolls into
 * view, then we start it. Playback path, in order of preference:
 *   1. Native HLS (Safari/iOS) — set src to the .m3u8, adaptive bitrate for free.
 *   2. hls.js (Chrome/Android/desktop) — lazy-load hls.light.min.js once and attach it
 *      to the .m3u8 with capLevelToPlayerSize, so phones pull a small rendition instead
 *      of the full 1080p MP4. Falls back to the MP4 on any fatal HLS error.
 *   3. Fallback to the MP4 source.
 *
 * Mobile: we don't PRE-load off-screen videos (rootMargin 0) so they don't all hit the
 * connection at once — but every video that's actually in view still autoplays (a hero
 * with two side-by-side videos must both play). Off-screen videos pause.
 */
(function () {
  if (window.__lazyVideoInit) return;
  window.__lazyVideoInit = true;

  var observed = typeof WeakSet !== 'undefined' ? new WeakSet() : null;
  var io = null;
  var muxIo = null;

  var isMobile = !!(window.matchMedia && window.matchMedia('(max-width: 749px)').matches);
  var ROOT_MARGIN = isMobile ? '0px 0px' : '200px 0px';

  // Lazy-load hls.js only when a non-native-HLS browser actually needs it.
  var hlsPromise = null;
  function loadHlsJs() {
    if (window.Hls) return Promise.resolve(window.Hls);
    if (hlsPromise) return hlsPromise;
    var url = window.__hlsJsUrl;
    if (!url) return Promise.resolve(null);
    hlsPromise = new Promise(function (resolve) {
      var s = document.createElement('script');
      s.src = url;
      s.onload = function () { resolve(window.Hls || null); };
      s.onerror = function () { resolve(null); };
      document.head.appendChild(s);
    });
    return hlsPromise;
  }

  function pickSources(v) {
    var hls = null, mp4 = null;
    v.querySelectorAll('source').forEach(function (s) {
      var u = s.dataset.src || s.getAttribute('src');
      if (!u) return;
      var type = (s.getAttribute('type') || '').toLowerCase();
      if (u.indexOf('.m3u8') > -1 || type.indexOf('mpegurl') > -1) { if (!hls) hls = u; }
      else if (!mp4) { mp4 = u; }
    });
    return { hls: hls, mp4: mp4 };
  }

  function play(v) {
    try { v.muted = true; } catch (e) {} // guarantee muted so mobile autoplay isn't blocked
    var p = v.play();
    if (p && p.catch) p.catch(function () {});
  }

  function setDirectSrc(v, url) {
    v.querySelectorAll('source').forEach(function (s) { s.removeAttribute('src'); });
    v.src = url;
    try { v.load(); } catch (e) {}
    play(v);
  }

  // Native HLS only when hls.js can't run. Chromium (incl. Arc) reports
  // canPlayType('application/vnd.apple.mpegurl') as "maybe" but CANNOT actually
  // decode HLS — setting the .m3u8 as src there plays poorly / shows the poster
  // (soft + color-shifted). So prefer hls.js (works on all MSE browsers) and use
  // native HLS only as the iOS-Safari fallback.
  function nativeOrMp4(v, s) {
    if (s.mp4) setDirectSrc(v, s.mp4);
    else if (s.hls && v.canPlayType && v.canPlayType('application/vnd.apple.mpegurl') !== '') setDirectSrc(v, s.hls);
  }

  function activate(v) {
    if (v.dataset.loaded) return;
    v.dataset.loaded = '1';
    var s = pickSources(v);

    // Desktop: play the progressive MP4, NOT HLS. Desktop Chromium (Chrome/Edge/
    // Arc) tints HLS video via its MSE hardware-decode path (a color-range bug)
    // even though the stream is correctly tagged bt709/tv — confirmed: desktop
    // Firefox + both mobiles are fine, only desktop Chromium casts. The MP4 path
    // decodes correctly, and bandwidth is a non-issue on desktop. Mobile keeps
    // adaptive HLS below (no tint there, and it saves data).
    if (!isMobile && s.mp4) {
      setDirectSrc(v, s.mp4);
      return;
    }

    if (s.hls && window.__hlsJsUrl) {
      loadHlsJs().then(function (Hls) {
        if (Hls && Hls.isSupported()) {
          // hls.js via MSE (Chromium/Arc/Firefox/desktop Safari). No
          // capLevelToPlayerSize — that locked onto the lowest rendition for
          // lazy/not-yet-laid-out players; default ABR picks the best the
          // connection supports.
          var inst = new Hls();
          inst.on(Hls.Events.ERROR, function (evt, data) {
            if (data && data.fatal) {
              try { inst.destroy(); } catch (e) {}
              nativeOrMp4(v, s); // HLS broke — fall back
            }
          });
          inst.loadSource(s.hls);
          inst.attachMedia(v);
          v._hls = inst;
          play(v);
        } else {
          // iOS Safari: no MSE for hls.js, but native HLS works.
          if (s.hls && v.canPlayType && v.canPlayType('application/vnd.apple.mpegurl') !== '') setDirectSrc(v, s.hls);
          else nativeOrMp4(v, s);
        }
      });
    } else if (s.hls && v.canPlayType && v.canPlayType('application/vnd.apple.mpegurl') !== '') {
      setDirectSrc(v, s.hls);
    } else {
      nativeOrMp4(v, s);
    }
  }

  function scan() {
    var videos = document.querySelectorAll('video[data-lazy-video]');
    if (!videos.length) return;

    if (!('IntersectionObserver' in window)) {
      videos.forEach(function (v) { activate(v); play(v); });
      return;
    }

    if (!io) {
      io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          var v = entry.target;
          if (entry.isIntersecting) {
            activate(v);
            play(v);
          } else {
            try { v.pause(); } catch (e) {}
          }
        });
      }, { rootMargin: ROOT_MARGIN, threshold: 0.01 });
    }

    videos.forEach(function (v) {
      if (observed) {
        if (observed.has(v)) return;
        observed.add(v);
      }
      io.observe(v);
    });
  }

  // Mux background videos (<mux-video data-lazy-mux>, rendered by snippets/home-video.liquid).
  // mux-video handles HLS + the rendition cap itself; here we just lazy-autoplay/pause it
  // like the native ones — preload="none" means nothing loads until it scrolls into view.
  function scanMux() {
    var muxes = document.querySelectorAll('mux-video[data-lazy-mux]');
    if (!muxes.length) return;
    if (!('IntersectionObserver' in window)) {
      muxes.forEach(function (m) { play(m); });
      return;
    }
    if (!muxIo) {
      muxIo = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) { play(entry.target); }
          else { try { entry.target.pause(); } catch (e) {} }
        });
      }, { rootMargin: ROOT_MARGIN, threshold: 0.01 });
    }
    muxes.forEach(function (m) {
      if (observed) { if (observed.has(m)) return; observed.add(m); }
      muxIo.observe(m);
    });
  }

  function init() { scan(); scanMux(); }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
})();
